/**
 * Contextual opt-in for desktop notifications (#285, decision D1).
 *
 * Lives inside the notification bell's dropdown, and that placement is the
 * whole point. `Notification.requestPermission()` can be answered "block", and
 * a blocked origin can never be un-blocked by the page — the app gets one ask
 * per user, for good. Spending it on page load is how sites end up permanently
 * unable to notify anyone. Asking here spends it on someone who has just opened
 * their notifications, which is the one moment the question answers itself.
 *
 * It shows only when there is something to gain:
 *  · web-push is configured on this environment (a VAPID key exists), and
 *  · the browser has no answer yet — `granted` needs no banner, `denied` cannot
 *    be lifted from here and would be a button that does nothing.
 *
 * Dismissal is remembered in `localStorage`, per browser rather than per
 * account: the permission itself is a browser-level fact, so a preference that
 * followed the user to another machine would hide the banner exactly where it
 * still had work to do.
 */

import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { AuthStore } from '@core/services/auth/auth.store';
import { DesktopNotificationsService } from '@core/services/infra/desktop-notifications.service';
import { FcmService } from '@core/services/infra/fcm.service';
import { UIFeedbackService } from '@shared/services/ui-feedback.service';

/** Why per-browser: the permission it advertises is itself per-browser. */
export const PUSH_OPT_IN_DISMISSED_KEY = 'proporio.pushOptIn.dismissed';

@Component({
  selector: 'app-push-opt-in-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, TranslatePipe],
  template: `
    @if (visible()) {
      <div class="push-opt-in" role="region" [attr.aria-label]="'shared.pushOptIn.title' | translate">
        <mat-icon class="push-opt-in-icon" svgIcon="mat:notifications_active"></mat-icon>
        <div class="push-opt-in-copy">
          <p class="push-opt-in-title">{{ 'shared.pushOptIn.title' | translate }}</p>
          <p class="push-opt-in-text">{{ 'shared.pushOptIn.body' | translate }}</p>
        </div>
        <div class="push-opt-in-actions">
          <button mat-button type="button" [disabled]="busy()" (click)="dismiss()">
            {{ 'shared.pushOptIn.later' | translate }}
          </button>
          <button mat-flat-button color="primary" type="button" [disabled]="busy()" (click)="enable()">
            {{ 'shared.pushOptIn.enable' | translate }}
          </button>
        </div>
      </div>
    }
  `,
  styles: [`
    .push-opt-in {
      display: flex;
      align-items: flex-start;
      gap: 0.625rem;
      flex-wrap: wrap;
      padding: 0.75rem 1rem;
      background: rgba(var(--vex-color-primary-rgb), 0.06);
      border-bottom: 1px solid rgba(var(--vex-color-primary-rgb), 0.2);
    }

    .push-opt-in-icon {
      flex: none;
      width: 20px;
      height: 20px;
      font-size: 20px;
      color: var(--vex-color-primary);
    }

    .push-opt-in-copy {
      flex: 1 1 12rem;
      min-width: 0;
    }

    .push-opt-in-title {
      margin: 0;
      font-size: 0.85rem;
      font-weight: 600;
    }

    .push-opt-in-text {
      margin: 0.125rem 0 0;
      font-size: 0.8rem;
      line-height: 1.35;
    }

    .push-opt-in-actions {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      margin-left: auto;
    }
  `],
})
export class PushOptInBannerComponent implements OnInit {
  private readonly fcm = inject(FcmService);
  private readonly desktopNotifications = inject(DesktopNotificationsService);
  private readonly authStore = inject(AuthStore);
  private readonly uiFeedback = inject(UIFeedbackService);
  private readonly translate = inject(TranslateService);

  readonly visible = signal(false);
  readonly busy = signal(false);

  ngOnInit(): void {
    this.visible.set(this.shouldShow());
  }

  private shouldShow(): boolean {
    if (!this.fcm.isConfigured()) return false;
    if (this.fcm.permissionState() !== 'default') return false;
    return !this.wasDismissed();
  }

  /**
   * Ask for the permission, then register.
   *
   * Runs from a click, which is the only context a browser accepts the prompt
   * in. Whatever the answer, the banner goes away: re-offering a question the
   * user has just answered — including by dismissing the prompt — is nagging.
   */
  async enable(): Promise<void> {
    const userId = this.authStore.user()?.userUID;
    if (!userId || this.busy()) return;
    this.busy.set(true);
    try {
      const result = await this.fcm.requestPermissionAndRegister(userId);
      if (result.token) {
        await this.desktopNotifications.start();
        this.toast('shared.pushOptIn.enabled');
      } else {
        this.toast('shared.pushOptIn.notEnabled');
      }
    } finally {
      this.rememberDismissed();
      this.visible.set(false);
      this.busy.set(false);
    }
  }

  /** "Later" hides it for good on this browser — the switch in the settings stays. */
  dismiss(): void {
    this.rememberDismissed();
    this.visible.set(false);
  }

  private wasDismissed(): boolean {
    try {
      return localStorage.getItem(PUSH_OPT_IN_DISMISSED_KEY) === '1';
    } catch {
      // Private mode / blocked storage: showing the banner is the safe failure.
      return false;
    }
  }

  private rememberDismissed(): void {
    try {
      localStorage.setItem(PUSH_OPT_IN_DISMISSED_KEY, '1');
    } catch {
      // Nothing to do — the banner reappears next session, which is survivable.
    }
  }

  private toast(key: string): void {
    this.uiFeedback.toast.info(this.translate.instant(key));
  }
}
